/**
 * Season scope token (mirror of services/importer-report include/scope_token.py).
 * `{DESTS}` = both origins (legacy key form); `{DESTS}__{PERU|CHILE}` = one origin.
 * DESTS = ALL or sorted dest tokens joined by `--`. Pure JS for `node --test`.
 */

export const ORIGIN_SEP = '__';
export const DEST_SEP = '--';
const ORIGINS = ['BOTH', 'PERU', 'CHILE'];
const RAW_OK = /^[A-Za-z0-9 _.-]+$/;
const TOKEN_RE = /^[A-Z][A-Z0-9_]{0,40}$/;

const ALIASES = {
  USA: 'USA',
  US: 'USA',
  'U.S': 'USA',
  'U.S.A': 'USA',
  'U.S.A.': 'USA',
  'UNITED STATES': 'USA',
  HOLLAND: 'NETHERLANDS',
  NETHERLANDS: 'NETHERLANDS',
  ENGLAND: 'UNITED_KINGDOM',
  UK: 'UNITED_KINGDOM',
  'UNITED KINGDOM': 'UNITED_KINGDOM',
  ALL: 'ALL',
};

/** Port of include/dest_token.py dest_token(); null when invalid. */
function destToken(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  let compact = s.replace(/[.-]+/g, ' ').toUpperCase();
  compact = compact.replace(/\s+/g, ' ').trim();
  if (compact === 'UNKNOWN' || compact === '') return null;
  if (Object.prototype.hasOwnProperty.call(ALIASES, compact)) return ALIASES[compact];
  let tok = compact.replace(/ /g, '_').replace(/-/g, '_');
  tok = tok.replace(/[^A-Z0-9_]/g, '');
  if (!tok || tok === 'UNKNOWN') return null;
  return TOKEN_RE.test(tok) ? tok : null;
}

function oneDest(raw) {
  const s = String(raw ?? '').trim();
  if (!s || s.includes(ORIGIN_SEP) || s.includes(DEST_SEP) || !RAW_OK.test(s)) {
    throw new Error(`invalid destination part: ${raw}`);
  }
  const tok = destToken(s);
  if (!tok || tok.includes(ORIGIN_SEP) || tok.startsWith('_') || tok.endsWith('_')) {
    throw new Error(`invalid destination part: ${raw}`);
  }
  return tok;
}

function canonDests(parts) {
  const toks = [...new Set(parts.map(oneDest))].sort();
  if (toks.length === 0) throw new Error('no destination');
  if (toks.includes('ALL') && toks.length > 1) {
    throw new Error('ALL cannot be combined with other destinations');
  }
  return toks;
}

/** DESTS string or array -> sorted unique dest tokens. */
export function parseDests(raw) {
  return canonDests(typeof raw === 'string' ? raw.split(DEST_SEP) : [...raw]);
}

function canonOrigin(origin) {
  const o = String(origin ?? 'BOTH').trim().toUpperCase() || 'BOTH';
  if (!ORIGINS.includes(o)) throw new Error(`invalid origin: ${origin}`);
  return o;
}

export function formatScope(dests, origin = 'BOTH') {
  const d = parseDests(dests).join(DEST_SEP);
  const o = canonOrigin(origin);
  return o === 'BOTH' ? d : `${d}${ORIGIN_SEP}${o}`;
}

/** Scope token -> { dests, origin }. Throws when invalid. */
export function parseScope(raw) {
  const s = String(raw ?? '').trim().toUpperCase();
  if (!s) throw new Error('empty scope');
  const parts = s.split(ORIGIN_SEP);
  if (parts.length > 2) throw new Error(`invalid scope: ${raw}`);
  let origin = 'BOTH';
  if (parts.length === 2) {
    origin = parts[1];
    if (origin !== 'PERU' && origin !== 'CHILE') {
      throw new Error(`invalid origin in scope: ${raw}`);
    }
  }
  return { dests: parseDests(parts[0]), origin };
}

/** /run style request: destination token plus optional origin. */
export function resolveRequest(destination, origin = null) {
  const { dests, origin: tokOrigin } = parseScope(destination);
  if (origin == null || String(origin).trim() === '') return { dests, origin: tokOrigin };
  const o = canonOrigin(origin);
  if (String(destination).includes(ORIGIN_SEP) && o !== tokOrigin) {
    throw new Error('origin disagrees with destination scope');
  }
  return { dests, origin: o };
}

/**
 * Scope token for the map selection, or '' when it cannot be built.
 * @param {string|string[]} destKeyOrCodes ALL | USA | CANADA--USA | ['CANADA','USA']
 * @param {string} originKey ALL | BOTH | PERU | CHILE (anything else -> '')
 */
export function buildSeasonScope(destKeyOrCodes, originKey = 'BOTH') {
  const o = String(originKey || '').toUpperCase();
  const origin = o === 'ALL' ? 'BOTH' : o;
  if (!ORIGINS.includes(origin)) return '';
  try {
    // NONE is the web's "nothing selected" sentinel, never a publishable dest.
    if (parseDests(destKeyOrCodes).includes('NONE')) return '';
    return formatScope(destKeyOrCodes, origin);
  } catch {
    return '';
  }
}
